import { useEffect, useState } from 'react';

interface FetchState {
    data: any;
    error: any;
    loading: null | boolean;
}

interface FetchOptions {
    method?: 'GET' | 'POST';
    headers?: Record<string, any>;
}

export const useFetch = (url: string, options: FetchOptions = {}, deps?: unknown[]): any => {
    const [data, setData] = useState<FetchState>({ data: null, error: null, loading: null });
    const headers: Record<string, any> = { 'X-Requested-With': 'XMLHttpRequest', ...(options.headers || {}) };

    const get = <T = any>(query?: any): Promise<T> => {
        let querySeparator = '?';
        let searchQuery = '';

        if (url.indexOf('?') >= 0) {
            querySeparator = '&';
        }

        if (query) {
            searchQuery = `${querySeparator}${Object.keys(query).map(key => `${key}=${encodeURIComponent(query[key])}`).join('&')}`;
        }

        // eslint-disable-next-line no-shadow
        setData(data => ({ ...data, loading: true }));

        return new Promise(success => {
            window.fetch(url + searchQuery, {
                method: 'GET',
                headers
            })
                .then(res => {
                    if (res.status !== 200) {
                        return Promise.reject(res);
                    }

                    return res.json();
                })
                .then(res => {
                    success(res);
                    setData(() => ({ data: res, error: null, loading: false }));
                })
                .catch(err => {
                    setData(() => ({ data: null, error: err, loading: false }));
                });
        });
    };

    // eslint-disable-next-line no-shadow
    const post = <T>(data?: any): Promise<T> => {
        let body: any;

        if (data) {
            if (data instanceof FormData) {
                body = data;
            } else if (typeof data === 'object') {
                body = JSON.stringify(data);
                headers['content-type'] = 'application/json';
            } else if (typeof data === 'string') {
                body = data;
            } else {
                throw new Error('Invalid data format');
            }
        }

        // eslint-disable-next-line no-shadow
        setData(data => ({ ...data, loading: true }));

        return new Promise((success, failure) => {
            window.fetch(url, {
                body,
                method: 'POST',
                headers
            })
                .then(res => res.json())
                .then(res => {
                    success(res);
                    setData(() => ({ data: res, error: null, loading: false }));
                })
                .catch(err => {
                    failure(err);
                    setData(() => ({ data: null, error: err, loading: false }));
                });
        });
    };

    useEffect(() => {
        if (deps !== void 0) {
            (options.method === 'POST' ? post : get)();
        }
    }, [...deps ?? [], url]);

    return {
        get,
        post,
        data: data.data,
        error: data.error,
        loading: data.loading
    };
};
